const safeJsonParse = (value) => {
    if (typeof value !== "string") return value;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

const parseRequestBody = (requestBody) => {
    const first = safeJsonParse(requestBody);
    if (first && typeof first === "object" && typeof first.data === "string") {
        return { ...first, data: safeJsonParse(first.data) };
    }

    return first;
};

const safeDecodeURIComponent = (value = "") => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const normalizeText = (value = "") => String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const isMeaningfulValue = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (value && typeof value === "object") return Object.keys(value).length > 0;
    return value !== undefined && value !== null && String(value).trim() !== "";
};

const formatFilterValue = (value) => {
    if (Array.isArray(value)) return value.join(", ");
    if (value && typeof value === "object") return normalizeText(JSON.stringify(value));
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
};

const toTitleCase = (value = "") => value
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getQueryParams = (query = "") => {
    if (!query) return {};
    if (query && typeof query === "object" && !Array.isArray(query)) return query;

    const normalizedQuery = query?.startsWith("?") ? query.slice(1) : query;
    const decodedQuery = safeDecodeURIComponent(normalizedQuery || "");
    const parsedQuery = safeJsonParse(decodedQuery);

    if (parsedQuery && typeof parsedQuery === "object" && !Array.isArray(parsedQuery)) {
        return parsedQuery;
    }

    return Object.fromEntries(new URLSearchParams(decodedQuery));
};

const getApiPathParams = (url = "") => {
    const conversationMatch = url.match(/conversation\/(\d+)/i);
    const reportMatch = url.match(/report\/(\d+)/i);
    const newsMatch = url.match(/news\/(\d+)/i);

    return {
        conversationID: conversationMatch?.[1] || "",
        reportID: reportMatch?.[1] || "",
        newsID: newsMatch?.[1] || "",
    };
};

const getModuleLabel = (log) => {
    const rawModule = log?.auditType || "";
    if (rawModule) return toTitleCase(rawModule);

    const url = (log?.url || "").toLowerCase();
    if (url.includes("conversation")) return "Chat";
    if (url.includes("download")) return "Download";
    if (url.includes("alerts/export")) return "Alert";

    return "-";
};

const getActionLabel = (log) => {
    const method = (log?.httpMethod || "").toUpperCase();
    const module = getModuleLabel(log);
    const url = (log?.url || "").toLowerCase();
    const requestBody = parseRequestBody(log?.requestBody ?? "");

    if (module === "Download") return "Download";
    if (url.includes("alerts/export")) return "Export";
    // if (url.includes("/logout")) return "Logout";
    // if (url.includes("/resend/otp")) return "Resend OTP";
    if (module === "Login") return "Login";
    if (module === "Chat" && requestBody?.isRead) return "Read";

    if (method === "POST") return "Create";
    if (method === "PUT" || method === "PATCH") return "Update";
    if (method === "DELETE") return "Delete";
    if (method === "GET") return "View";

    return method || "-";
};

const getDisplayModuleLabel = (log) => {
    const module = getModuleLabel(log);
    const queryParams = getQueryParams(log?.query || "");
    const requestBody = parseRequestBody(log?.requestBody ?? "");
    const downloadType = queryParams?.type || requestBody?.type || "";

    if (module === "Download" && downloadType) return toTitleCase(downloadType);

    return module;
};

const getCombinedActionLabel = (log) => {
    const module = getDisplayModuleLabel(log);
    const action = getActionLabel(log);

    if (!action || action === "-") return module || "-";
    if (!module || module === "-" || module === "Login") return action;
    if (module.toLowerCase() === action.toLowerCase()) return action;

    return `${module} ${action.toLowerCase()}`;
};

const getDownloadTarget = (queryParams, pathParams) => {
    const type = (queryParams?.type || "").toLowerCase();
    const entityId = queryParams?.id || pathParams?.reportID || pathParams?.newsID || "";
    const alertID = queryParams?.alertID;

    if (!type || !entityId) return null;

    if (type === "news") {
        return { label: `News-${entityId}`, to: `/news-details?id=${entityId}&from=audit-log` };
    }
    if (type === "report") {
        return { label: `Report-${entityId}`, to: `/report-detail?id=${entityId}&from=audit-log` };
    }
    if (type === "alert") {
        return { label: `${entityId}`, to: `/alert-details/${entityId}?from=audit-log`};
    }
    if (type === "response") {
        return { label: `${alertID}`, to: `/alert-details/${alertID}?from=audit-log`, responseID: entityId };
    }

    return null;
};

const buildDownloadDescription = (log, requestBody, queryParams, pathParams) => {
    const fileName = requestBody?.name || requestBody?.fileName || requestBody?.filename || "";
    let typeLabel = queryParams?.type ? toTitleCase(queryParams.type) : "file";
    const entityId = queryParams?.id || pathParams?.reportID || pathParams?.newsID || "";
    const isFullReport = String(queryParams?.isReport || "").toLowerCase() === "true";
    const target = getDownloadTarget(queryParams, pathParams);
    typeLabel = target?.responseID ? `Alert ${typeLabel}` : typeLabel;

    if (typeLabel === "Report" && isFullReport) {
        return {
            prefix: "Downloaded full report",
            target,
            suffix: fileName ? ` - ${fileName}` : "",
            wrapTarget: true,
        };
    }

    if (target) {
        return {
            prefix: fileName ? `Downloaded file "${fileName}" from ${typeLabel}` : `Downloaded file from ${typeLabel}`,
            target,
            suffix: "",
            wrapTarget: true,
        };
    }

    if (fileName && entityId) return `Downloaded ${typeLabel} (ID: ${entityId}) - ${fileName}`;
    if (fileName) return `Downloaded ${typeLabel} - ${fileName}`;
    if (entityId) return `Downloaded ${typeLabel} (ID: ${entityId})`;

    return `Downloaded ${typeLabel}`;
};

const buildChatDescription = (log, requestBody, queryParams, pathParams) => {
    const data = requestBody?.data && typeof requestBody.data === "object" ? requestBody.data : requestBody;
    const conversationID = data?.conversationID || queryParams?.id || pathParams?.conversationID || "";
    const groupID = data?.groupID || queryParams?.groupID || "";
    if (data?.isRead) {
        if (conversationID && groupID) {
            return {
                prefix: "Marked",
                target: {
                    label: log?.conversationTitle ? log?.conversationTitle :`Conversation-${conversationID}`,
                    to: `/conversation/${conversationID}/group/${groupID}?from=audit-log`,
                },
                suffix: " as read",
            };
        }

        return conversationID
            ? `Marked Conversation (ID: ${conversationID}) as read`
            : "Marked conversation as read";
    }

    return "Updated chat activity";
};

const buildAlertExportDescription = (requestBody) => {
    if (!requestBody || typeof requestBody !== "object") return "Exported alerts";

    const customerLocations = Array.isArray(requestBody.customerLocations)
        ? requestBody.customerLocations
            .map((location) => {
                if (!location?.name) return "";
                if (Array.isArray(location.states) && location.states.length) {
                    return `${location.name} (${location.states.join(", ")})`;
                }
                return location.name;
            })
            .filter(Boolean)
        : [];

    const filterLabels = [
        ["keyword", "Keyword"],
        ["alertStatus", "Status"],
        ["sports", "Sports"],
        ["groups", "Groups"],
        ["tags", "Tags"],
        ["gender", "Gender"],
        ["channel", "Channel"],
        ["alertType", "Alert Type"],
        ["locations", "Locations"],
        ["customerLocations", "Customer Locations"],
        ["timeOfBet", "Time Of Bet"],
        ["stateOfBet", "State Of Bet"],
        ["timeOfAlert", "Time Of Alert"],
        ["responseType", "Response Type"],
        ["tournaments", "Tournaments"],
        ["tournamentLevels", "Tournament Levels"],
        ["eSportsTitles", "eSports Titles"],
        ["eventStartDate", "Event Start Date"],
        ["eventEndDate", "Event End Date"],
        ["startDate", "Start Date"],
        ["endDate", "End Date"],
        ["maxBets", "Max Bets"],
        ["sort", "Sort"],
    ];

    const filters = filterLabels
        .filter(([key]) => key === "customerLocations"
            ? customerLocations.length > 0
            : isMeaningfulValue(requestBody[key]))
        .map(([key, label]) => {
            const value = key === "customerLocations" ? customerLocations : requestBody[key];
            return `${label}: ${formatFilterValue(value)}`;
        });

    return filters.length
        ? `Exported alerts with filters - ${filters.join(" | ")}`
        : "Exported alerts";
};

const buildAuditDescription = (log) => {
    const requestBody = parseRequestBody(log?.requestBody ?? "");
    const queryParams = getQueryParams(log?.query || "");
    const pathParams = getApiPathParams(log?.url || "");
    const module = getModuleLabel(log);
    const url = (log?.url || "").toLowerCase();

    if (module === "Download") return buildDownloadDescription(log, requestBody, queryParams, pathParams);
    if (module === "Chat") return buildChatDescription(log, requestBody, queryParams, pathParams);
    if (url.includes("alerts/export")) return buildAlertExportDescription(requestBody);
    if (url.includes("/logout")) return "Logged out";
    if (url.includes("/resend/otp")) return log?.statusCode !== 200 ? "OTP resend failed" : "OTP resent";
    if (url.includes("/login/otp")) return log?.statusCode !== 200 ? "Login failed" : "Logged in";
    if (url.endsWith("/login")) return log?.statusCode !== 200 ? "Invalid credentials" : "Login initiated";

    if (requestBody && typeof requestBody === "object") {
        const summary = normalizeText(JSON.stringify(requestBody));
        return summary || "-";
    }

    return safeDecodeURIComponent(log?.query || log?.url || "-");
};
const getEmailLabel = (log) => {
    const requestBody = parseRequestBody(log?.requestBody ?? "");
    return log?.email || requestBody?.email || "-";
}

export const normalizeAuditLogs = (payload) => {
    const source = Array.isArray(payload) ? payload : [];

    return [ ...source.map((log, index) => ({
        ...log,
        id: log?.id ?? `${log?.url || "log"}-${index}`,
        createdAt: log?.createdAt || "",
        email: getEmailLabel(log),
        ipAddress: log?.ipAddress || "-",
        module: getDisplayModuleLabel(log),
        action: getCombinedActionLabel(log),
        description: buildAuditDescription(log),
        statusCode: log?.statusCode || "-",
    }))];
};
